import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatPaginator, MatPaginatorIntl, PageEvent } from "@angular/material/paginator";
import { MatDialog } from "@angular/material/dialog";
import { Subscription, Subject, from, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from "rxjs/operators";
import {
  CriticalPointTemplateSearchParams,
  CriticalPointTemplateSearchResponse,
  CriticalPointTemplateService,
} from "../../services/critical-point-template.service";
import { CriticalPointTemplate } from "src/app/classes/model/practice-path";
import { ConfirmationDialogService } from "src/app/modules/confirmation-dialog/services/confirmation-dialog.service";
import {
  CriticalPointTemplateEditDialogComponent,
  CriticalPointTemplateEditDialogData,
} from "../critical-point-template-edit-dialog/critical-point-template-edit-dialog.component";

@Component({
  selector: "app-critical-point-template-page",
  templateUrl: "./critical-point-template-page.component.html",
  styleUrls: ["./critical-point-template-page.component.scss"],
})
export class CriticalPointTemplatePageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  protected readonly pageSizeOptions: number[] = [10, 25, 50];
  protected filterForm: FormGroup;
  protected sortForm: FormGroup;
  protected templates: CriticalPointTemplate[] = [];
  protected totalCount = 0;
  protected isLoading = false;
  protected hasError = false;

  // Expose current filter values for highlighting
  protected get titleFilter(): string {
    return this.filterForm.get("title")?.value?.trim() ?? "";
  }

  protected get descriptionFilter(): string {
    return this.filterForm.get("description")?.value?.trim() ?? "";
  }

  protected readonly sortOptions = [
    { value: "createdAt-desc", label: "Létrehozás dátuma (újabb elől)" },
    { value: "createdAt-asc", label: "Létrehozás dátuma (régebbi elől)" },
    { value: "usageCount-desc", label: "Használat száma (több elől)" },
    { value: "usageCount-asc", label: "Használat száma (kevesebb elől)" },
  ];

  private readonly destroy$ = new Subject<void>();
  private readonly loadTemplatesSubject$ = new Subject<void>();
  private loadTemplatesSubscription: Subscription;
  private currentPage = 1;
  protected pageSize = this.pageSizeOptions[0];

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly templateService: CriticalPointTemplateService,
    private readonly matPaginatorIntl: MatPaginatorIntl,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly dialog: MatDialog
  ) {
    this.filterForm = this.formBuilder.group({
      title: [""],
      description: [""],
    });

    this.sortForm = this.formBuilder.group({
      sortOption: ["createdAt-desc"],
    });
  }

  ngOnInit(): void {
    this.matPaginatorIntl.itemsPerPageLabel = "";

    this.filterForm.valueChanges
      .pipe(
        debounceTime(400),
        map(() => this.getNormalizedFilters()),
        distinctUntilChanged(
          (prev, curr) =>
            prev.titleContains === curr.titleContains &&
            prev.descriptionContains === curr.descriptionContains
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.resetToFirstPage();
        this.triggerLoadTemplates();
      });

    this.sortForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetToFirstPage();
        this.triggerLoadTemplates();
      });

    this.loadTemplatesSubscription = this.loadTemplatesSubject$
      .pipe(
        switchMap(() => {
          this.isLoading = true;
          this.hasError = false;
          const params = this.buildSearchParams();
          return from(this.templateService.searchTemplates(params)).pipe(
            catchError(() => {
              return of(undefined);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response: CriticalPointTemplateSearchResponse | undefined) => {
        this.isLoading = false;
        if (!response) {
          this.handleErrorState();
          return;
        }
        this.handleSuccessState(response);
      });

    this.triggerLoadTemplates();
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.paginator.pageSize = this.pageSize;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadTemplatesSubscription?.unsubscribe();
  }

  protected onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.triggerLoadTemplates();
  }

  protected clearFilter(field: "title" | "description"): void {
    if (!this.filterForm.get(field)) {
      return;
    }
    if (!this.filterForm.get(field)?.value) {
      return;
    }
    this.filterForm.patchValue({ [field]: "" });
  }

  protected onCreateTemplate(): void {
    const dialogData: CriticalPointTemplateEditDialogData = {
      mode: "create",
    };

    const dialogRef = this.dialog.open(CriticalPointTemplateEditDialogComponent, {
      width: "600px",
      data: dialogData,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: CriticalPointTemplate | undefined) => {
      if (result) {
        // Reset to first page and reload
        this.resetToFirstPage();
        this.triggerLoadTemplates();
      }
    });
  }

  protected onEditTemplate(template: CriticalPointTemplate): void {
    const dialogData: CriticalPointTemplateEditDialogData = {
      mode: "edit",
      template: template,
    };

    const dialogRef = this.dialog.open(CriticalPointTemplateEditDialogComponent, {
      width: "600px",
      data: dialogData,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: CriticalPointTemplate | undefined) => {
      if (result) {
        // Reload the current page
        this.triggerLoadTemplates();
      }
    });
  }

  protected async onDeleteTemplate(template: CriticalPointTemplate): Promise<void> {
    this.confirmationDialogService.open(
      "Sablon törlése",
      `Biztosan törölni szeretnéd a(z) "${template.title}" sablont? Ez a művelet nem vonható vissza.`,
      async () => {
        await this.deleteTemplate(template.id);
      },
      undefined,
      "Törlés",
      "Mégse"
    );
  }

  private async deleteTemplate(templateId: string): Promise<void> {
    const success = await this.templateService.deleteTemplate(templateId);

    if (success) {
      // If we're on a page other than the first and this was the last item on the page,
      // go back one page
      const isLastItemOnPage = this.templates.length === 1;
      const isNotFirstPage = this.currentPage > 1;

      if (isLastItemOnPage && isNotFirstPage) {
        this.currentPage--;
        if (this.paginator) {
          this.paginator.pageIndex = this.currentPage - 1;
        }
      }

      // Reload templates
      this.triggerLoadTemplates();
    }
  }

  private triggerLoadTemplates(): void {
    this.loadTemplatesSubject$.next();
  }

  private resetToFirstPage(): void {
    this.currentPage = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  private getNormalizedFilters(): Partial<CriticalPointTemplateSearchParams> {
    const title = this.filterForm.get("title")?.value?.trim() ?? "";
    const description = this.filterForm.get("description")?.value?.trim() ?? "";

    return {
      titleContains: title.length > 0 ? title : undefined,
      descriptionContains: description.length > 0 ? description : undefined,
    };
  }

  private buildSearchParams(): CriticalPointTemplateSearchParams {
    const normalizedFilters = this.getNormalizedFilters();
    const sortOption = this.sortForm.get("sortOption")?.value ?? "createdAt-desc";
    const [sortBy, sortOrder] = sortOption.split("-") as [
      "createdAt" | "usageCount",
      "asc" | "desc"
    ];

    return {
      page: this.currentPage,
      pageSize: this.pageSize,
      sortBy,
      sortOrder,
      ...normalizedFilters,
    };
  }

  private handleErrorState(): void {
    this.hasError = true;
    this.templates = [];
    this.totalCount = 0;
    if (this.paginator) {
      this.paginator.length = 0;
      this.paginator.pageIndex = 0;
    }
  }

  private handleSuccessState(response: CriticalPointTemplateSearchResponse): void {
    this.hasError = false;
    this.templates = response.items ?? [];
    this.totalCount = response.totalCount ?? 0;
    this.currentPage = response.page ?? 1;
    this.pageSize = response.pageSize ?? this.pageSize;

    if (this.paginator) {
      this.paginator.length = this.totalCount;
      this.paginator.pageIndex = Math.max(this.currentPage - 1, 0);
      this.paginator.pageSize = this.pageSize;
    }
  }
}

